import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createTheme, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material";

export type ThemeMode = "light" | "dark";

// Design tokens: a minimal navy + white palette shared across the whole app,
// with a navy-on-near-black variant for night mode.
export const navy = {
  900: "#0B2038",
  800: "#0F2C4C",
  700: "#123761",
  600: "#1B4B7F", // light-mode primary
  500: "#2B6099",
  400: "#4C86C2", // dark-mode primary
  100: "#E7EDF5",
  50: "#F4F7FB",
};
const neutral = {
  0: "#FFFFFF",
  50: "#F7F9FC",
  100: "#F1F4F8",
  200: "#E4E9F0",
  300: "#D2D9E3",
  600: "#5B6B7F",
  700: "#3F4C5E",
  900: "#141C28",
};
const darkSurface = {
  default: "#0A1420",
  paper: "#0F2038",
  border: "rgba(255,255,255,0.09)",
  textPrimary: "#F3F6FA",
  textSecondary: "#93A5BB",
};

export function buildTheme(mode: ThemeMode): Theme {
  const isDark = mode === "dark";
  const primaryMain = isDark ? navy[400] : navy[600];
  const borderColor = isDark ? darkSurface.border : neutral[200];
  const paperColor = isDark ? darkSurface.paper : neutral[0];
  const defaultBg = isDark ? darkSurface.default : neutral[50];
  const textSecondary = isDark ? darkSurface.textSecondary : neutral[600];

  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryMain,
        light: isDark ? "#6FA3D6" : navy[500],
        dark: isDark ? navy[600] : navy[800],
        contrastText: isDark ? "#0A1420" : neutral[0],
      },
      secondary: {
        main: isDark ? navy[500] : navy[800],
        contrastText: neutral[0],
      },
      background: {
        default: defaultBg,
        paper: paperColor,
      },
      text: {
        primary: isDark ? darkSurface.textPrimary : neutral[900],
        secondary: textSecondary,
      },
      divider: borderColor,
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h4: { fontWeight: 700, letterSpacing: "-0.01em" },
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 600 },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: defaultBg,
          },
        },
      },
      // Flat, bordered cards instead of heavy drop shadows.
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: 14,
            border: `1px solid ${borderColor}`,
            boxShadow: "none",
            backgroundImage: "none",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
          outlined: {
            borderColor,
          },
        },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: paperColor,
            color: isDark ? darkSurface.textPrimary : neutral[900],
            borderBottom: `1px solid ${borderColor}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: paperColor,
            borderRight: `1px solid ${borderColor}`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 16,
            backgroundImage: "none",
            backgroundColor: paperColor,
            boxShadow: isDark
              ? "0 20px 48px rgba(0,0,0,0.5)"
              : "0 20px 48px rgba(11,32,56,0.16)",
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 700,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            backgroundColor: isDark ? alpha("#FFFFFF", 0.04) : neutral[50],
            color: textSecondary,
            fontWeight: 600,
            fontSize: "0.75rem",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            borderBottom: `1px solid ${borderColor}`,
          },
          root: {
            borderBottom: `1px solid ${isDark ? darkSurface.border : neutral[100]}`,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:last-child .MuiTableCell-root": {
              borderBottom: "none",
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 600,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: paperColor,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: isDark ? borderColor : neutral[300],
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: primaryMain,
            },
          },
        },
      },
      // Style for all buttons
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            padding: "6px 16px",
            transition: "all 0.15s ease",
            "&:hover": {
              boxShadow: "none",
            },
          },
          contained: {
            boxShadow: "none",
            "&:hover": {
              boxShadow: isDark
                ? "0 4px 10px rgba(0,0,0,0.4)"
                : "0 4px 10px rgba(11,32,56,0.18)",
            },
          },
          outlined: {
            borderColor: isDark ? borderColor : neutral[300],
            "&:hover": {
              borderColor: primaryMain,
              backgroundColor: alpha(primaryMain, isDark ? 0.12 : 0.06),
            },
          },
        },
      },
      // Style for all icon buttons
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: "all 0.15s ease",
            padding: "8px",
            borderRadius: "10px",
          },
        },
      },
      // Style for tooltips
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: "0.75rem",
            padding: "6px 10px",
            backgroundColor: isDark ? "#F3F6FA" : neutral[900],
            color: isDark ? "#0A1420" : "#FFFFFF",
          },
          arrow: {
            color: isDark ? "#F3F6FA" : neutral[900],
          },
        },
        defaultProps: {
          arrow: true,
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            "& .MuiPaginationItem-root": {
              borderRadius: 8,
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            backgroundColor: isDark ? alpha("#FFFFFF", 0.08) : neutral[100],
          },
        },
      },
    },
  });
}

type ThemeModeContextValue = {
  mode: ThemeMode;
  toggleMode: () => void;
};

const ThemeModeContext = createContext<ThemeModeContextValue>({
  mode: "light",
  toggleMode: () => {},
});

const STORAGE_KEY = "amoozical-admin-theme-mode";

function readStoredMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore storage failures (e.g. private browsing)
    }
  }, [mode]);

  const toggleMode = () =>
    setMode((prev) => (prev === "light" ? "dark" : "light"));

  const value = useMemo(() => ({ mode, toggleMode }), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
